import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Heart, Shield, Award, Users, CheckCircle, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { coreValues } from "../data/mockData";

export const About = () => {
  const iconMap = {
    Heart: Heart,
    Shield: Shield,
    Award: Award,
    Users: Users,
  };

  return (
    <div data-testid="about-page" className="pt-24">
      {/* Hero Section */}
      <section className="py-16 relative">
        <div className="absolute top-0 right-0 w-96 h-96 bg-secondary/20 rounded-full blur-3xl -z-10" />
        
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs tracking-widest uppercase font-bold text-primary/60 mb-4 block">
                About Us
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-primary leading-tight mb-6">
                Dedicated to Compassionate Home Care
              </h1>
              <p className="text-muted-foreground text-lg leading-relaxed mb-6">
                Konastone Healthcare Ltd is a trusted domiciliary care provider based in 
                Tipton, England. We are committed to offering high-quality, person-centred 
                home care services that promote independence, dignity, and comfort.
              </p>
              <p className="text-muted-foreground leading-relaxed">
                Our dedicated team of care professionals provides compassionate support 
                tailored to each client's unique needs, ensuring that individuals can 
                continue living fulfilling lives in the comfort of their own homes.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <img
                src="https://images.unsplash.com/photo-1576765608535-5f04d1e3f289?w=700"
                alt="Compassionate care"
                data-testid="about-hero-image"
                className="w-full h-[400px] object-cover rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="section-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://images.unsplash.com/photo-1447069387593-a5de0862481e?w=600"
                alt="Our mission"
                className="w-full h-[350px] object-cover rounded-2xl shadow-lg"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs tracking-widest uppercase font-bold text-primary/60 mb-4 block">
                Our Mission
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-primary mb-6">
                Empowering Independence Through Quality Care
              </h2>
              <p className="text-muted-foreground leading-relaxed mb-6">
                Our mission is to deliver exceptional home care services that empower 
                individuals to maintain their independence while receiving the support 
                they need. We believe everyone deserves to live with dignity in the 
                place they call home.
              </p>
              <ul className="space-y-3">
                {[
                  "Person-centred care tailored to individual needs",
                  "Promoting independence and self-determination",
                  "Supporting families and carers",
                  "Continuous improvement and excellence",
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="text-xs tracking-widest uppercase font-bold text-primary/60 mb-3 block">
              What We Stand For
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-primary mb-4">
              Our Core Values
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              These principles guide our approach to care and define who we are 
              as an organization.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {coreValues.map((value, index) => {
              const IconComponent = iconMap[value.icon];
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  data-testid={`about-value-${index}`}
                  className="bg-white border border-primary/10 rounded-xl p-8 flex gap-6 hover:shadow-lg transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    {IconComponent && <IconComponent className="w-8 h-8 text-primary" />}
                  </div>
                  <div>
                    <h3 className="font-serif font-semibold text-primary text-xl mb-2">
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {value.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-primary text-white">
        <div className="section-container">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <span className="text-xs tracking-widest uppercase font-bold text-white/60 mb-4 block">
                Our Story
              </span>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold mb-6">
                Built on Experience, Driven by Care
              </h2>
              <p className="text-white/80 text-lg leading-relaxed mb-6">
                Konastone Healthcare was founded with a simple yet powerful vision: 
                to provide the kind of care we would want for our own loved ones. 
                Our founder experienced firsthand the challenges of finding quality 
                home care and knew there had to be a better way.
              </p>
              <p className="text-white/80 leading-relaxed mb-8">
                Today, we're proud to serve families across Tipton and the West Midlands, 
                delivering personalized care with professionalism and heart. Our team 
                is not just qualified – they're passionate about making a real difference 
                in people's lives every single day.
              </p>
              <Link to="/team">
                <Button
                  data-testid="meet-team-btn"
                  className="bg-white text-primary hover:bg-white/90 rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105"
                >
                  Meet Our Team
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-white border border-primary/10 rounded-2xl p-8 md:p-12 text-center"
          >
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-primary mb-4">
              Ready to Start Your Care Journey?
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
              Contact us today for a free, no-obligation care assessment. 
              We're here to help you find the perfect care solution.
            </p>
            <Link to="/contact">
              <Button
                data-testid="about-cta-btn"
                className="bg-accent hover:bg-accent/90 text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105 shadow-lg"
              >
                Book Free Assessment
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
